import { useState } from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { Add16Regular, ChevronDown16Regular, ChevronRight16Regular } from "@fluentui/react-icons";
import { FuiButton } from "@/components/fui-control";

const useStyles = makeStyles({
  container: {
    marginTop: "8px",
    paddingTop: "6px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
    "@media print": {
      display: "none !important",
    },
  },
  toggleButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "2px 4px",
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  count: {
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: "tabular-nums",
  },
  drawerList: {
    marginTop: "4px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  drawerItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    minHeight: "28px",
    padding: "2px 4px",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  itemText: {
    flexGrow: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
  },
  itemSecondary: {
    marginLeft: "4px",
    color: tokens.colorNeutralForeground3,
  },
});

export interface SuggestionItem {
  id: string;
  label: string;
  secondary?: string;
}

export interface SuggestionDrawerProps {
  items: SuggestionItem[];
  onRestore: (id: string) => void;
  label?: string;
  className?: string;
}

export function SuggestionDrawer({
  items,
  onRestore,
  label = "제외된 항목",
  className,
}: SuggestionDrawerProps) {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  if (!items || items.length === 0) return null;

  return (
    <div className={`${styles.container} ${className || ""}`} data-print-hide="" data-suggestion-drawer="">
      <button
        type="button"
        className={styles.toggleButton}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        {isOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
        <span>{label}</span>
        <span className={styles.count}>{items.length}</span>
      </button>

      {isOpen ? (
        <div className={styles.drawerList}>
          {items.map((item) => (
            <div key={item.id} className={styles.drawerItem}>
              <span className={styles.itemText} title={item.label}>
                {item.label}
                {item.secondary ? <span className={styles.itemSecondary}>{item.secondary}</span> : null}
              </span>
              <FuiButton appearance="subtle" icon={<Add16Regular />} onClick={() => onRestore(item.id)}>
                포함
              </FuiButton>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
